class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initRecommendationSliders();
    this.initRecommendationCards();
    this.initRecommendationAddButtons();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.initRecommendationSliders();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.initRecommendationSliders();
      this.initRecommendationCards();
      this.initRecommendationAddButtons();
      this.open();
    });
  }

  initRecommendationSliders() {
    this.querySelectorAll('[data-new-cart-drawer-slider]').forEach((slider) => {
      if (slider.dataset.newCartDrawerDragReady === 'true') return;
      slider.dataset.newCartDrawerDragReady = 'true';

      let isDragging = false;
      let didDrag = false;
      let startX = 0;
      let startScrollLeft = 0;

      const stopDrag = () => {
        if (!isDragging) return;
        isDragging = false;
        slider.classList.remove('is-dragging');
      };

      slider.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('[data-new-cart-drawer-add], button, input, select, textarea')) return;
        if (slider.scrollWidth <= slider.clientWidth) return;

        isDragging = true;
        didDrag = false;
        startX = event.clientX;
        startScrollLeft = slider.scrollLeft;
        slider.classList.add('is-dragging');

        if (typeof slider.setPointerCapture === 'function') {
          slider.setPointerCapture(event.pointerId);
        }
      });

      slider.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });

      slider.addEventListener('pointermove', (event) => {
        if (!isDragging) return;

        const distance = event.clientX - startX;
        if (Math.abs(distance) > 3) didDrag = true;
        slider.scrollLeft = startScrollLeft - distance;

        if (didDrag) event.preventDefault();
      });

      slider.addEventListener('pointerup', stopDrag);
      slider.addEventListener('pointercancel', stopDrag);
      slider.addEventListener('pointerleave', stopDrag);

      slider.addEventListener(
        'click',
        (event) => {
          if (!didDrag) return;
          event.preventDefault();
          event.stopPropagation();
          didDrag = false;
        },
        true
      );
    });
  }

  initRecommendationCards() {
    this.querySelectorAll('[data-new-cart-drawer-card-url]').forEach((card) => {
      if (card.dataset.newCartDrawerCardReady === 'true') return;
      card.dataset.newCartDrawerCardReady = 'true';

      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, form, input, select, textarea')) return;
        const url = card.dataset.newCartDrawerCardUrl;
        if (url) window.location.href = url;
      });
    });
  }

  initRecommendationAddButtons() {
    this.querySelectorAll('[data-new-cart-drawer-add]').forEach((button) => {
      if (button.dataset.newCartDrawerAddReady === 'true') return;
      button.dataset.newCartDrawerAddReady = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.addRecommendationToCart(button);
      });
    });
  }

  addRecommendationToCart(button) {
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;
    this.setActiveElement(button);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw response;

        if (window.PUB_SUB_EVENTS && window.publish) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'new-cart-drawer-recommendation',
            productVariantId: variantId,
            cartData: response,
          });
        }

        this.renderContents(response);
      })
      .catch((error) => {
        const errors = this.querySelector('#CartDrawer-CartErrors');
        if (errors) errors.textContent = error && error.description ? error.description : window.cartStrings.error;
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
